import { useCallback, useEffect, useRef, useState } from 'react';

import { auth } from '@atlaskit/outbound-auth-flow-client/auth';
import { AuthError } from '@atlaskit/outbound-auth-flow-client/error';

import {
	type TSearch3pConfigAPIResponse,
	update3pConfigCacheEntry,
} from '@atlassian/search-client/fetch-search-config';

import {
	CONNECTOR_OAUTH_ABORT_REASON,
	CONNECTOR_OAUTH_CANCELLATION_REASON,
	CONNECTOR_OAUTH_ERROR_SUBTYPE,
	CONNECTOR_OAUTH_STATUS,
	type ConnectorOAuthErrorSubtype,
	type ConnectorOAuthLifecycleEvent,
	type ConnectorOAuthStatus,
	type UseConnectorOAuthOptions,
	type UseConnectorOAuthResult,
} from './connectorOAuthTypes.ts';

const AUTH_ERROR_TYPE = {
	AUTH_WINDOW_CLOSED: 'auth_window_closed',
	WINDOW_BLOCKED: 'window_blocked',
} as const;

const CONNECTOR_OAUTH_WINDOW_WIDTH = 800;
const CONNECTOR_OAUTH_WINDOW_HEIGHT = 800;

type ConnectorOAuthWindowMetrics = Pick<
	Window,
	'outerHeight' | 'outerWidth' | 'screenX' | 'screenY'
>;

const getBrowserWindowMetrics = (): ConnectorOAuthWindowMetrics => ({
	outerHeight: window.outerHeight,
	outerWidth: window.outerWidth,
	screenX: window.screenX,
	screenY: window.screenY,
});

export const getConnectorOAuthWindowFeatures = ({
	outerHeight,
	outerWidth,
	screenX,
	screenY,
}: ConnectorOAuthWindowMetrics): string => {
	const left = Math.round(screenX + (outerWidth - CONNECTOR_OAUTH_WINDOW_WIDTH) / 2);
	const top = Math.round(screenY + (outerHeight - CONNECTOR_OAUTH_WINDOW_HEIGHT) / 2);

	return `popup=yes,width=${CONNECTOR_OAUTH_WINDOW_WIDTH},height=${CONNECTOR_OAUTH_WINDOW_HEIGHT},left=${left},top=${top},status=no,resizable=yes,scrollbars=yes`;
};

const getAuthErrorType = (authError: unknown): string | undefined =>
	authError instanceof AuthError ? authError.type : undefined;

const getError = (error: unknown): Error =>
	error instanceof Error ? error : new Error('Unknown connector OAuth error');

const getInitialStatus = (userNeedsOAuth: boolean | undefined): ConnectorOAuthStatus =>
	userNeedsOAuth === false ? CONNECTOR_OAUTH_STATUS.CONNECTED : CONNECTOR_OAUTH_STATUS.IDLE;

const markConnectorAsAuthed = (
	config: TSearch3pConfigAPIResponse,
	resourceType: string,
): TSearch3pConfigAPIResponse =>
	config.map((resource) =>
		resource.type === resourceType
			? {
					...resource,
					isUserOAuthed: true,
					userNeedsOAuth: false,
				}
			: resource,
	);

export const useConnectorOAuth = ({
	connector,
	cloudId,
	userId,
	onLifecycleEvent,
}: UseConnectorOAuthOptions): UseConnectorOAuthResult => {
	const { outboundAuthUrl, productKey, resourceType, userNeedsOAuth } = connector;
	const [status, setStatus] = useState<ConnectorOAuthStatus>(() =>
		getInitialStatus(userNeedsOAuth),
	);
	const [errorSubtype, setErrorSubtype] = useState<ConnectorOAuthErrorSubtype | null>(null);
	const [remountKey, setRemountKey] = useState(0);
	const statusRef = useRef(status);
	const isMountedRef = useRef(false);
	const onLifecycleEventRef = useRef(onLifecycleEvent);
	onLifecycleEventRef.current = onLifecycleEvent;

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const emitLifecycleEvent = useCallback(
		(event: ConnectorOAuthLifecycleEvent) => {
			onLifecycleEventRef.current?.(event, { productKey, resourceType });
		},
		[productKey, resourceType],
	);

	const setStatusAndRef = useCallback((nextStatus: ConnectorOAuthStatus) => {
		statusRef.current = nextStatus;
		setStatus(nextStatus);
	}, []);

	useEffect(() => {
		if (userNeedsOAuth === false && statusRef.current !== CONNECTOR_OAUTH_STATUS.CONNECTED) {
			/* eslint-disable @atlassian/perf-linting/no-chain-state-updates -- Preserve the existing OAuth state transition during extraction. */
			setErrorSubtype(null);
			setStatusAndRef(CONNECTOR_OAUTH_STATUS.CONNECTED);
			/* eslint-enable @atlassian/perf-linting/no-chain-state-updates */
		}
	}, [setStatusAndRef, userNeedsOAuth]);

	const transitionToIdleFromCancel = useCallback(() => {
		if (!isMountedRef.current) {
			return;
		}

		const previousStatus = statusRef.current;
		setErrorSubtype(null);
		setStatusAndRef(CONNECTOR_OAUTH_STATUS.IDLE);

		if (previousStatus !== CONNECTOR_OAUTH_STATUS.IDLE) {
			setRemountKey((key) => key + 1);
		}
	}, [setStatusAndRef]);

	const connect = useCallback(async () => {
		if (
			!cloudId ||
			!userId ||
			!outboundAuthUrl ||
			statusRef.current === CONNECTOR_OAUTH_STATUS.CONNECTING ||
			statusRef.current === CONNECTOR_OAUTH_STATUS.CONNECTED
		) {
			return;
		}

		setErrorSubtype(null);
		setStatusAndRef(CONNECTOR_OAUTH_STATUS.CONNECTING);

		const authStartTime = Date.now();
		emitLifecycleEvent({ type: 'auth-started' });

		try {
			await auth(outboundAuthUrl, getConnectorOAuthWindowFeatures(getBrowserWindowMetrics()));
		} catch (authError) {
			const authErrorType = getAuthErrorType(authError);

			if (authErrorType === AUTH_ERROR_TYPE.AUTH_WINDOW_CLOSED) {
				emitLifecycleEvent({
					type: 'auth-cancelled',
					reason: CONNECTOR_OAUTH_CANCELLATION_REASON.USER_CLOSED_WINDOW,
				});
				transitionToIdleFromCancel();
				return;
			}

			if (!isMountedRef.current) {
				emitLifecycleEvent({
					type: 'auth-aborted',
					reason: CONNECTOR_OAUTH_ABORT_REASON.UNMOUNTED_DURING_OAUTH,
				});
				return;
			}

			const error = getError(authError);
			const nextErrorSubtype =
				authErrorType === AUTH_ERROR_TYPE.WINDOW_BLOCKED
					? CONNECTOR_OAUTH_ERROR_SUBTYPE.POPUP_BLOCKED
					: CONNECTOR_OAUTH_ERROR_SUBTYPE.GENERIC;

			if (nextErrorSubtype === CONNECTOR_OAUTH_ERROR_SUBTYPE.POPUP_BLOCKED) {
				setErrorSubtype(nextErrorSubtype);
				emitLifecycleEvent({
					type: 'auth-aborted',
					reason: CONNECTOR_OAUTH_ABORT_REASON.POPUP_BLOCKED,
				});
			} else {
				emitLifecycleEvent({
					type: 'auth-error-observed',
					error,
				});
				setErrorSubtype(nextErrorSubtype);
			}

			setStatusAndRef(CONNECTOR_OAUTH_STATUS.ERROR);
			emitLifecycleEvent({
				type: 'auth-failed',
				durationMs: Date.now() - authStartTime,
				error,
				errorSubtype: nextErrorSubtype,
			});

			throw error;
		}

		if (isMountedRef.current) {
			setErrorSubtype(null);
			setStatusAndRef(CONNECTOR_OAUTH_STATUS.CONNECTED);
			emitLifecycleEvent({
				type: 'auth-succeeded',
				durationMs: Date.now() - authStartTime,
			});
		} else {
			emitLifecycleEvent({
				type: 'auth-aborted',
				reason: CONNECTOR_OAUTH_ABORT_REASON.RESOLVED_AFTER_UNMOUNT,
			});
		}

		if (!resourceType) {
			return;
		}

		try {
			await update3pConfigCacheEntry(userId, cloudId, (config) =>
				markConnectorAsAuthed(config, resourceType),
			);
		} catch (cacheError) {
			emitLifecycleEvent({
				type: 'cache-update-failed',
				error: getError(cacheError),
			});
		}
	}, [
		cloudId,
		emitLifecycleEvent,
		outboundAuthUrl,
		resourceType,
		setStatusAndRef,
		transitionToIdleFromCancel,
		userId,
	]);

	return {
		status,
		errorSubtype,
		remountKey,
		connect,
	};
};
